import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import api from '../../utils/api';
import { toast } from 'react-hot-toast';

const AddProject = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialTemplateId = searchParams.get('template');
  const initialClientId = searchParams.get('client');
  const initialPlanId = searchParams.get('plan');
  
  const [formData, setFormData] = useState({
    name: '',
    client_id: initialClientId || '',
    description: '',
    start_date: '',
    due_date: '',
    budget: '',
    department_id: '',
    group_id: '',
    type_id: '',
  });
  
  const [useTemplate, setUseTemplate] = useState(!!initialTemplateId);
  const [templates, setTemplates] = useState([]);
  const [clients, setClients] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [groups, setGroups] = useState([]);
  const [types, setTypes] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(initialTemplateId || '');
  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(true);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  
  // Fetch project plan data if plan ID is provided
  useEffect(() => {
    if (initialPlanId && initialClientId) {
      const fetchPlanData = async () => {
        try {
          const response = await api.get(`/clients/${initialClientId}/project-plans/${initialPlanId}`);
          
          if (response.data && response.data.plan) {
            const plan = response.data.plan;
            
            // Pre-fill form data from the plan
            setFormData(prevData => {
              const updatedData = {
                ...prevData,
                name: plan.name || prevData.name,
                client_id: initialClientId,
                description: plan.description || prevData.description,
              };
              
              // Handle dates if they exist in the plan
              if (plan.start_date) {
                try {
                  const dateValue = new Date(plan.start_date);
                  if (!isNaN(dateValue.getTime())) {
                    updatedData.start_date = dateValue.toISOString();
                  }
                } catch (err) {
                  console.error('Error parsing start_date:', err);
                }
              }
              
              if (plan.due_date) {
                try {
                  const dateValue = new Date(plan.due_date);
                  if (!isNaN(dateValue.getTime())) {
                    updatedData.due_date = dateValue.toISOString();
                  }
                } catch (err) {
                  console.error('Error parsing due_date:', err);
                }
              }
              
              return updatedData;
            });
            
            toast.success('Project plan data loaded');
          }
        } catch (err) {
          console.error('Error fetching project plan data:', err);
          // Don't set error state here - just log it
        }
      };
      
      fetchPlanData();
    }
  }, [initialPlanId, initialClientId]);
  
  // Fetch clients and templates data
  useEffect(() => {
    const fetchData = async () => {
      setFetchingData(true);
      setError(null);
      
      try {
        // Fetch clients
        try {
          // Only fetch active clients
          const clientsResponse = await api.get('/clients?active_only=true');
          let clientsData = [];
          
          if (Array.isArray(clientsResponse.data)) {
            clientsData = clientsResponse.data;
          } else if (clientsResponse.data && Array.isArray(clientsResponse.data.clients)) {
            clientsData = clientsResponse.data.clients;
          } else if (clientsResponse.data && typeof clientsResponse.data === 'object') {
            console.warn('Unexpected clients response format:', clientsResponse.data);
          }
          
          // Filter out archived clients (should be done on the server, but adding as a safeguard)
          clientsData = clientsData.filter(client => !client.is_archived);
          
          setClients(clientsData);
        } catch (err) {
          console.error('Error fetching clients:', err);
          setError('Failed to load clients. Some features may be unavailable.');
        }
        
        // Fetch templates
        try {
          const templatesResponse = await api.get('/projects/templates');
          let templatesData = [];
          
          if (Array.isArray(templatesResponse.data)) {
            templatesData = templatesResponse.data;
          } else if (templatesResponse.data && Array.isArray(templatesResponse.data.templates)) {
            templatesData = templatesResponse.data.templates;
          } else if (templatesResponse.data && typeof templatesResponse.data === 'object') {
            console.warn('Unexpected templates response format:', templatesResponse.data);
          }
          
          setTemplates(templatesData);
          
          // Select the first template by default if available and initialTemplateId not provided
          if (templatesData.length > 0 && !initialTemplateId) {
            setSelectedTemplate(templatesData[0]._id || templatesData[0].id);
          }
          
          // If initialTemplateId is provided, check if it exists in the templates
          if (initialTemplateId) {
            const templateExists = templatesData.some(t => 
              (t._id === initialTemplateId || t.id === parseInt(initialTemplateId))
            );
            
            if (templateExists) {
              setSelectedTemplate(initialTemplateId);
            } else {
              console.warn(`Template with ID ${initialTemplateId} not found.`);
            }
          }
        } catch (err) {
          console.error('Error fetching templates:', err);
          setError((prevError) => 
            prevError ? `${prevError} Also failed to load templates.` : 'Failed to load templates. Some features may be unavailable.'
          );
        }

        // Fetch departments
        try {
          const deptResponse = await api.get('/departments');
          if (deptResponse.data && deptResponse.data.departments) {
            setDepartments(deptResponse.data.departments);
            // Set default department if it exists
            if (deptResponse.data.departments.length > 0) {
              setFormData(prevState => ({
                ...prevState,
                department_id: deptResponse.data.departments[0].id
              }));
            }
          }
        } catch (err) {
          console.error('Error fetching departments:', err);
          setError((prevError) => 
            prevError ? `${prevError} Also failed to load departments.` : 'Failed to load departments. Some features may be unavailable.'
          );
        }

        // Fetch groups
        try {
          const groupResponse = await api.get('/groups');
          if (groupResponse.data && groupResponse.data.groups) {
            setGroups(groupResponse.data.groups);
            // Set default group if it exists
            if (groupResponse.data.groups.length > 0) {
              setFormData(prevState => ({
                ...prevState,
                group_id: groupResponse.data.groups[0].id
              }));
            }
          }
        } catch (err) {
          console.error('Error fetching groups:', err);
          setError((prevError) => 
            prevError ? `${prevError} Also failed to load groups.` : 'Failed to load groups. Some features may be unavailable.'
          );
        }

        // Fetch types
        try {
          const typeResponse = await api.get('/types');
          if (typeResponse.data && typeResponse.data.types) {
            setTypes(typeResponse.data.types);
            // Set default type if it exists
            if (typeResponse.data.types.length > 0) {
              setFormData(prevState => ({
                ...prevState,
                type_id: typeResponse.data.types[0].id
              }));
            }
          }
        } catch (err) {
          console.error('Error fetching types:', err);
          setError((prevError) => 
            prevError ? `${prevError} Also failed to load types.` : 'Failed to load types. Some features may be unavailable.'
          );
        }
      } finally {
        setFetchingData(false);
      }
    };
    
    fetchData();
  }, [initialTemplateId, initialClientId]);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    // Additional validation for dates
    if (!formData.start_date) {
      setError('Start date is required');
      setLoading(false);
      return;
    }
    
    if (!formData.due_date) {
      setError('Due date is required');
      setLoading(false);
      return;
    }
    
    // Create a copy of formData to ensure we don't modify the original state
    const dataToSubmit = { ...formData };
    
    // Only rename date fields when NOT using a template (different API endpoints expect different formats)
    if (!useTemplate) {
      // Rename date fields to match what the server expects for regular projects
      if (dataToSubmit.start_date) {
        dataToSubmit.startDate = dataToSubmit.start_date;
        delete dataToSubmit.start_date;
      }
      
      if (dataToSubmit.due_date) {
        dataToSubmit.dueDate = dataToSubmit.due_date;
        delete dataToSubmit.due_date;
      }
    }
    
    // Log the data being sent to the server for debugging
    console.log('Submitting project data with dates:', {
      startDate: dataToSubmit.startDate,
      dueDate: dataToSubmit.dueDate,
      start_date: dataToSubmit.start_date,
      due_date: dataToSubmit.due_date
    });
    
    try {
      let response;
      let projectData;
      
      if (useTemplate && selectedTemplate) {
        // Create from template
        console.log(`Creating project from template ID: ${selectedTemplate}`);
        response = await api.post(`/projects/from-template/${selectedTemplate}`, dataToSubmit);
        console.log('Template project creation response:', response.data);
        projectData = response.data.project || response.data;
      } else {
        // Create from scratch
        console.log('Creating project from scratch');
        response = await api.post('/projects', dataToSubmit);
        console.log('Project creation response:', response.data);
        // Extract project from the response based on API structure
        projectData = response.data.project || response.data;
      }
      
      console.log("Project data received:", projectData);
      
      // Extract the project ID with better error handling
      let projectId;
      if (projectData && (projectData.id || projectData._id)) {
        projectId = projectData.id || projectData._id;
        console.log(`Extracted project ID: ${projectId}`);
      } else {
        console.error('Could not extract project ID from response:', projectData);
        setError('Project was created but the ID could not be determined. Please check the projects list.');
        navigate('/projects'); // Redirect to projects list instead
        return;
      }
      
      // Ensure projectId is not undefined
      if (!projectId) {
        console.error('Project ID is undefined, redirecting to projects list');
        navigate('/projects');
      } else {
        console.log(`Navigating to project details: /projects/${projectId}`);
        navigate(`/projects/${projectId}`);
      }
    } catch (err) {
      console.error('Error creating project:', err);
      if (err.response) {
        console.error('Error response data:', err.response.data);
        setError(`Failed to create project: ${err.response.data.error || err.response.statusText}`);
      } else {
        setError('Failed to create project. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div>
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-gray-900">Add New Project</h1>
        <Link
          to="/projects"
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          Cancel
        </Link>
      </div>
      
      {error && (
        <div className="mt-4 bg-red-50 border-l-4 border-red-400 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}
      
      {fetchingData ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
        </div>
      ) : (
        <div className="mt-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Template Selection */}
            <div className="bg-white shadow px-4 py-5 sm:rounded-lg sm:p-6">
              <div className="md:grid md:grid-cols-3 md:gap-6">
                <div className="md:col-span-1">
                  <h3 className="text-lg font-medium leading-6 text-gray-900">Project Template</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Choose whether to create a project from a template or from scratch.
                  </p>
                </div>
                <div className="mt-5 md:mt-0 md:col-span-2">
                  <div className="space-y-6">
                    <div className="flex items-center">
                      <input
                        id="use-template"
                        name="template-option"
                        type="radio"
                        checked={useTemplate}
                        onChange={() => setUseTemplate(true)}
                        className="h-4 w-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
                      />
                      <label htmlFor="use-template" className="ml-3 block text-sm font-medium text-gray-700">
                        Create from template
                      </label>
                    </div>
                    
                    <div className="flex items-center">
                      <input
                        id="no-template"
                        name="template-option"
                        type="radio"
                        checked={!useTemplate}
                        onChange={() => setUseTemplate(false)}
                        className="h-4 w-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
                      />
                      <label htmlFor="no-template" className="ml-3 block text-sm font-medium text-gray-700">
                        Create from scratch
                      </label>
                    </div>
                    
                    {useTemplate && (
                      <div className="mt-4">
                        <label htmlFor="template" className="block text-sm font-medium text-gray-700">
                          Select template
                        </label>
                        <select
                          id="template"
                          name="template"
                          value={selectedTemplate}
                          onChange={(e) => setSelectedTemplate(e.target.value)}
                          className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                          disabled={loading || templates.length === 0}
                        >
                          {templates.length === 0 ? (
                            <option value="">No templates available</option>
                          ) : (
                            templates.map((template) => (
                              <option key={template._id || template.id} value={template._id || template.id}>
                                {template.name}
                              </option>
                            ))
                          )}
                        </select>
                        {templates.length > 0 && selectedTemplate && (
                          <div>
                            <p className="mt-2 text-sm text-gray-500">
                              {templates.find(t => t._id === selectedTemplate || t.id === parseInt(selectedTemplate))?.description || ''}
                            </p>
                            <p className="mt-1 text-sm text-indigo-600">
                              This template includes default RACI assignments that will be applied to your project.
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          
            <div className="bg-white shadow px-4 py-5 sm:rounded-lg sm:p-6">
              <div className="md:grid md:grid-cols-3 md:gap-6">
                <div className="md:col-span-1">
                  <h3 className="text-lg font-medium leading-6 text-gray-900">Project Information</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Enter the basic information about the project.
                  </p>
                </div>
                <div className="mt-5 md:mt-0 md:col-span-2">
                  <div className="grid grid-cols-6 gap-6">
                    <div className="col-span-6 sm:col-span-4">
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                        Project name
                      </label>
                      <input
                        type="text"
                        name="name"
                        id="name"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                      />
                    </div>
                    
                    <div className="col-span-6 sm:col-span-4">
                      <label htmlFor="client_id" className="block text-sm font-medium text-gray-700">
                        Client
                      </label>
                      <select
                        id="client_id"
                        name="client_id"
                        required
                        value={formData.client_id}
                        onChange={handleChange}
                        className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      >
                        <option value="">Select a client</option>
                        {clients.map((client) => (
                          <option key={client.id || client._id} value={client.id || client._id}>
                            {client.name}
                          </option>
                        ))}
                      </select>
                      {clients.length === 0 && (
                        <p className="mt-2 text-sm text-red-500">
                          No clients available. Please create a client first.
                        </p>
                      )}
                    </div>
                    
                    <div className="col-span-6 sm:col-span-4">
                      <label htmlFor="department_id" className="block text-sm font-medium text-gray-700">
                        Department
                      </label>
                      <select
                        id="department_id"
                        name="department_id"
                        value={formData.department_id}
                        onChange={handleChange}
                        className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      >
                        <option value="">Select a department</option>
                        {departments.map((department) => (
                          <option key={department.id} value={department.id}>
                            {department.name}
                          </option>
                        ))}
                      </select>
                      {departments.length === 0 && (
                        <p className="mt-2 text-sm text-gray-500">
                          No departments available. Default will be used.
                        </p>
                      )}
                    </div>
                    
                    <div className="col-span-6">
                      <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                        Description
                      </label>
                      <textarea
                        id="description"
                        name="description"
                        rows={3}
                        value={formData.description}
                        onChange={handleChange}
                        className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                      />
                    </div>
                    
                    <div className="col-span-6 sm:col-span-3">
                      <label htmlFor="start_date" className="block text-sm font-medium text-gray-700">
                        Start date *
                      </label>
                      <input
                        type="date"
                        name="start_date"
                        id="start_date"
                        required
                        value={formData.start_date}
                        onChange={handleChange}
                        className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                      />
                    </div>
                    
                    <div className="col-span-6 sm:col-span-3">
                      <label htmlFor="due_date" className="block text-sm font-medium text-gray-700">
                        Due date *
                      </label>
                      <input
                        type="date"
                        name="due_date"
                        id="due_date"
                        required
                        value={formData.due_date}
                        onChange={handleChange}
                        className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                      />
                    </div>
                    
                    <div className="col-span-6 sm:col-span-3">
                      <label htmlFor="budget" className="block text-sm font-medium text-gray-700">
                        Budget ($)
                      </label>
                      <input
                        type="number"
                        name="budget"
                        id="budget"
                        min="0"
                        step="1000"
                        value={formData.budget}
                        onChange={handleChange}
                        className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-white shadow px-4 py-5 sm:rounded-lg sm:p-6">
              <div className="md:grid md:grid-cols-3 md:gap-6">
                <div className="md:col-span-1">
                  <h3 className="text-lg font-medium leading-6 text-gray-900">Group</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Select the group for the project.
                  </p>
                </div>
                <div className="mt-5 md:mt-0 md:col-span-2">
                  <div className="grid grid-cols-6 gap-6">
                    <div className="col-span-6 sm:col-span-4">
                      <label htmlFor="group_id" className="block text-sm font-medium text-gray-700">
                        Group
                      </label>
                      <select
                        id="group_id"
                        name="group_id"
                        value={formData.group_id || ''}
                        onChange={handleChange}
                        className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      >
                        <option value="">Select a group</option>
                        {groups.map((group) => (
                          <option key={group.id} value={group.id}>
                            {group.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-white shadow px-4 py-5 sm:rounded-lg sm:p-6">
              <div className="md:grid md:grid-cols-3 md:gap-6">
                <div className="md:col-span-1">
                  <h3 className="text-lg font-medium leading-6 text-gray-900">Type</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Select the type for the project.
                  </p>
                </div>
                <div className="mt-5 md:mt-0 md:col-span-2">
                  <div className="grid grid-cols-6 gap-6">
                    <div className="col-span-6 sm:col-span-4">
                      <label htmlFor="type_id" className="block text-sm font-medium text-gray-700">
                        Type
                      </label>
                      <select
                        id="type_id"
                        name="type_id"
                        value={formData.type_id || ''}
                        onChange={handleChange}
                        className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      >
                        <option value="">Select a type</option>
                        {types.map((type) => (
                          <option key={type.id} value={type.id}>
                            {type.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex justify-end">
              <Link
                to="/projects"
                className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 mr-3"
              >
                Cancel
              </Link>
              <button
                type="submit"
                disabled={loading || clients.length === 0}
                className={`inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${
                  loading || clients.length === 0 ? 'bg-indigo-300 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700'
                }`}
              >
                {loading ? 'Creating...' : 'Create Project'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default AddProject; 